import _ from 'lodash';
import JSZip from 'jszip';
import {saveAs} from 'file-saver';
import {pdf} from '@react-pdf/renderer';
import {getDateFormat} from '../../utils/suitableDate';
import {API_GET_DRIVER_BILLINGS} from '../../constants/apisPath';
import {sendGetRequest} from '../../utils/apiService';
import {CANCELED, DRIVER, ORDER_COMPLETED, ORDER_SUCCESS} from '../../constants';
import store from '../../utils/store';
import {getCompletedOrders} from '../../apis/backend';
import React, {ReactElement} from 'react';
import {ExtendedDriverData} from 'src/models/drivers/driver';
import {DriverModalState} from './DriverModal';
import {BillingDetails, BillingTransaction} from "../../models/Billing";
import {roundNumber} from "../../utils/numberUtils";
import {DRIVER_INVOICE_DATE_FORMAT} from "../../constants/Invoice";

type RenderDriverInvoiceFunction = (selectedBilling: BillingTransaction) => ReactElement

const download = (zip: JSZip, driver: ExtendedDriverData) => {
  const { firstName, lastName } = driver;
  zip.generateAsync({ type: 'blob' }).then(function(blob) {
    saveAs(blob, `${firstName}${lastName}.zip`);
  });
};

export const hasDriverInvoiceData = (billing: BillingTransaction): boolean => {
  const inputs = Object.values(billing?.accounting?.inputs ?? billing?.inputs ?? {});
  return inputs.some((input: BillingDetails) => input?.type === DRIVER);
};

export const generateZip = (charges: string[], driver: ExtendedDriverData, billings: BillingTransaction[], renderDriverInvoice: RenderDriverInvoiceFunction) => {
  const zip = JSZip();  
  for (const chargeId of charges) {
    const billingItem: BillingTransaction = _.find(billings, (bill: BillingTransaction) => bill.chargeId === chargeId);
    if (billingItem && hasDriverInvoiceData(billingItem)) {
      const { dateOrder, orderNumber } = billingItem;
      zip.file(
        `factures/${getDateFormat(dateOrder as any, DRIVER_INVOICE_DATE_FORMAT)}${orderNumber}.pdf`,
        pdf(renderDriverInvoice(billingItem)).toBlob()
      );
    }
  }
  download(zip, driver);
};

export const handleSelectAllClick = (
  event: React.ChangeEvent<HTMLInputElement>, 
  rows: BillingTransaction[],
  setComponentState: React.Dispatch<React.SetStateAction<DriverModalState>>) => {
  if (event.target.checked) {
    const newSelection = _.map(rows, (bill: BillingTransaction) => bill.chargeId);
    setComponentState(prevState => ({ ...prevState, selected: newSelection }));
  } else {
    setComponentState(prevState => ({...prevState, selected: []}));
  }
};

export const handleClick = (chargeId: string, selected: string[], setComponentState: React.Dispatch<React.SetStateAction<DriverModalState>>) => {
  const selectedIndex = selected.indexOf(chargeId);
  let newSelected: string[] = [];
  if (selectedIndex === -1) {
    newSelected = newSelected.concat(selected, chargeId);
  } else if (selectedIndex === 0) {
    newSelected = newSelected.concat(selected.slice(1));
  } else if (selectedIndex === selected.length - 1) {
    newSelected = newSelected.concat(selected.slice(0, -1));
  } else if (selectedIndex > 0) {
    newSelected = newSelected.concat(
      selected.slice(0, selectedIndex),
      selected.slice(selectedIndex + 1)
    );
  }
  setComponentState(prevState => ({ ...prevState, selected: newSelected }));
};

export const getDriverBillingsByShopId = async (driverId: string) => {
  const { shopId } = store.getState().user;
    const response = await sendGetRequest(
      `${API_GET_DRIVER_BILLINGS}/${driverId}`
      , { shopId },
      { }
    );
    return response.data
  
};

export const getDataValue = (value: string) => {
  return value || '--'
};

export const initDriverData = async (
  driver: ExtendedDriverData, 
  shopId: string, 
  isIndependentTab: boolean, 
  setDriverState: React.Dispatch<React.SetStateAction<ExtendedDriverData | null>>, 
  setBillings: React.Dispatch<React.SetStateAction<BillingTransaction[]>>,
  setComponentState: React.Dispatch<React.SetStateAction<DriverModalState>>):Promise<void> => {

  let fees = 0;
  let canceledOrders = 0;
  const driverBillings = isIndependentTab ? await getDriverBillingsByShopId(driver.id) : await getCompletedOrders(shopId, 'idDep', driver.id);
  _.map(driverBillings, (billing: BillingTransaction) => {
    if (billing.shopId === shopId) {
      const isSuccessOrder = (billing.status === ORDER_SUCCESS || billing.status === ORDER_COMPLETED) && billing.orderNumber
      if (isSuccessOrder) {
        if (driver?.duration >= 0) {
          driver.duration += parseFloat(String(billing?.delivery?.duration ?? billing?.duration ?? 0));
        }
        if (driver?.distance >= 0) {
          driver.distance += parseFloat(String(billing?.delivery?.distance ?? billing?.distance! ?? 0));
        }
        fees += parseFloat(String(billing?.delivery?.fee));
      }
      if (billing?.cancellation && billing?.status === CANCELED) {
        canceledOrders++;
        driver.cancelNb = canceledOrders;
      }
    }
  });
  setDriverState({
    ...driver,
    fee: roundNumber(fees),
    speed: driver?.duration > 0 ?
      Math.round((driver.distance / driver.duration) * 6000) / 100
      : 0,
    durationPerOrder: roundNumber(driver.duration / driver.nbOrderSuccess) || 0,
    distancePerOrder: roundNumber(driver.distance / driver.nbOrderSuccess) || 0, })
  setBillings(
    _.orderBy(
      _.filter(
        driverBillings,
        (bill: BillingTransaction) => bill.orderNumber && bill.status === ORDER_SUCCESS
      ),
      ['dateOrder', 'orderNumber'],
      ['desc', 'desc']
    ))
  setComponentState(prevState => ({ ...prevState, isLoading: true }))
};